import React from 'react';

import { SideMenu } from '@vlabs/uikit';

import {
  AnalyticsTypeDropdown,
  SynchronizeStatus,
} from '@vlabs/monokit/entities/sources';
import { SectionErrorIcon } from '@vlabs/monokit/shared/ui/side-nav-layout';

import { useSourceStreamSettingsNavTranslation } from './i18n';
import st from './SourceStreamSettingsNav.module.sass';
import type {
  SourceStreamAnalyticsItem,
  SourceStreamSection,
} from './types';

interface SourceStreamSettingsNavProps {
  activeSection: SourceStreamSection;
  onChange: (section: SourceStreamSection) => void;
  sectionErrors?: Partial<Record<SourceStreamSection, boolean>>;
  analyticsItems: SourceStreamAnalyticsItem[];
  onAddAnalytics?: (type: string) => void;
}

function SourceStreamSettingsNav({
  activeSection,
  onChange,
  sectionErrors = {},
  analyticsItems,
  onAddAnalytics = undefined,
}: SourceStreamSettingsNavProps): React.JSX.Element {
  const { t } = useSourceStreamSettingsNavTranslation();

  return (
    <React.Fragment>
      <SideMenu.MenuItem
        data-testid="source-stream-nav-main"
        isActive={activeSection === 'main'}
        onClick={() => onChange('main')}
        rightIcon={<SectionErrorIcon hasError={!!sectionErrors.main} />}
        value={t('sections.main')}
      />
      <SideMenu.MenuItemDivider />

      <SideMenu.MenuItem
        data-testid="source-stream-nav-va-group"
        isOpened
        value={t('va_group')}
      >
        {analyticsItems.map((item) => (
          <SideMenu.MenuItem
            data-testid={`source-stream-nav-${item.key}`}
            isActive={activeSection === item.key}
            key={item.key}
            onClick={() => onChange(item.key)}
            rightIcon={(
              <React.Fragment>
                {!!item.syncStatus && (
                  <SynchronizeStatus synchronized={item.syncStatus === 'synced'} />
                )}
                <SectionErrorIcon hasError={!!sectionErrors[item.key]} />
              </React.Fragment>
            )}
            value={item.title}
          />
        ))}

        {!!onAddAnalytics && (
          <div className={st.AddAnalytics}>
            <AnalyticsTypeDropdown
              addLabel={t('add_analytics')}
              data-testid="source-stream-nav-add-analytics"
              onAdd={onAddAnalytics}
            />
          </div>
        )}
      </SideMenu.MenuItem>
    </React.Fragment>
  );
}

export { SourceStreamSettingsNav };
